import { useState, useMemo, useEffect, useRef } from 'react';

import AutoCompleteSelect from 'components/Input/AutoCompleteSelect';
import CustomerProductDetails from './CustomerProductDetails';
import { useAppContext } from 'services/context';
import { useTranslation } from 'services/hooks';
import { COURSE_IDS, NOTIFICATION_TYPES, ROUTES, VALIDATIONS } from 'constants/index.js';
import { generateId } from 'services/utils';
import { ChangesSpinner } from 'components/Spinner';
import Button from 'components/Button';
import Container from 'components/Container';
import Input from 'components/Input';
import Select from 'components/Input/Select';
import Tooltip from 'components/Tooltip';
import Icon from 'components/Icon';
import Table from 'pages/ManagementPanel/Table';

import './style.css';

const AdvancedUserManagement = () => {
	const { Api, ddls, responseMessage, addNotification } = useAppContext();

	const [wordEntered, setWordEntered] = useState('');
	const [errorMessageSide, setErrorMessageSide] = useState('');
	const [usersList, setUsersList] = useState([]);
	const [selectedUser, setSelectedUser] = useState({});
	const [selectedCourse, setSelectedCourse] = useState([]);
	const [enrollOrderValue, setEnrollOrderValue] = useState('');

	const [requestUpdateUsers, setRequestUpdateUsers] = useState([]);
	const [updateRequestSelectedStudent, setUpdateRequestSelectedStudent] = useState({});
	const [updateRequestOriginalData, setUpdateRequestOriginalData] = useState({});
	const [updateRequestSendParameters, setUpdateRequestSendParameters] = useState({});
	const [updateRequestApprovedUserFields, setUpdateRequestApprovedUserFields] = useState([]);

	const [transferCourseData, setTransferCourseData] = useState({ userId: null, coursesList: [] });
	const getTranslation = useTranslation();

	const TRANSLATION_KEY = 'advanced_user_management';
	const VALIDATIONS_TRANSLATION_KEY = 'validations';

	const LABEL_TYPE = Object.freeze({
		fname: getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_label_type_fname`),
		lname: getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_label_type_lname`),
		phone: getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_label_type_phone`),
		email: getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_label_type_email`),
	});

	const requestUpdateUsersColumns = useMemo(() => {
		return [
			{
				Header: ' ',
				columns: [
					{
						//running number - rows counter
						Header: '#',
						// accessor: 'userId',
						Cell: row => <>{row.row.index + 1}</>,
					},
					{
						Header: getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_email_column`),
						accessor: 'email',
						Cell: row => <a href={`mailto:${row.value}`}>{row.value}</a>,
					},
					{
						Header: getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_treat_column`),
						Cell: ({ row }) => (
							<span className="treat" onClick={() => _handleNewUpdateRequestSelectStudent(row)}>
								<Icon icon="editIcon" />
							</span>
						),
					},
				],
			},
		];
	}, [getTranslation]);

	const toolTipRef = useRef();

	//export user course list
	const selectedUserCoursesList = useMemo(() => {
		if (!selectedUser) return [];
		return selectedUser.courses || [];
	}, [selectedUser]);

	const isIELTS = useMemo(() => {
		if (!selectedCourse) return false;
		return selectedCourse.courseId === COURSE_IDS.IELTS || selectedCourse.courseId === COURSE_IDS.IELTS_GENERAL;
	}, [selectedCourse]);

	const isTOEFL = useMemo(() => {
		if (!selectedCourse) return false;
		return selectedCourse.courseId === COURSE_IDS.TOEFL || selectedCourse.courseId === COURSE_IDS.TOEFL_IBT;
	}, [selectedCourse]);

	const isTEF = useMemo(() => {
		if (!selectedCourse) return false;
		return selectedCourse.courseId === COURSE_IDS.TEF;
	}, [selectedCourse]);

	const isESL = useMemo(() => {
		if (!selectedCourse) return false;
		return (
			selectedCourse.courseId === COURSE_IDS.ESL_A1 ||
			selectedCourse.courseId === COURSE_IDS.ESL_B1 ||
			selectedCourse.courseId === COURSE_IDS.ESL_C1
		);
	}, [selectedCourse]);

	//export user course emails by selected course
	const selectedCourseEmails = useMemo(() => {
		if (!selectedCourse) return [];
		const { courseEmails } = selectedCourse;
		if (!isIELTS && !isTEF && !isESL && !isTOEFL) return courseEmails || [];

		const courseEmailsCopy = [...courseEmails];

		if (isIELTS || isTEF || isTOEFL) {
			const resetIELTSObj = {
				courseId: selectedCourse.courseId,
				emailTypeId: 'reset_timer',
				emailName: getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_reset_timer_email_name`),
			};
			courseEmailsCopy.push(resetIELTSObj);
		}
		// if (isESL) {
		// 	const resetActivationCodeObj = {
		// 		courseId: selectedCourse.courseId,
		// 		emailTypeId: 'reset_code',
		// 		emailName: getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_reset_code_email_name`),
		// 	};
		// 	courseEmailsCopy.push(resetActivationCodeObj);
		// }

		return courseEmailsCopy;
	}, [selectedCourse, getTranslation]);

	//handle user product status active/inactive
	const _handleToggleUserProductStatus = (active, id) => {
		if (active === null) return;

		const userCourses = selectedUser?.courses?.map(course => {
			const { isActive: _active, recordId } = course;
			let updatedActive = _active;
			if (id === recordId) updatedActive = active;
			return { ...course, isActive: updatedActive, id: recordId };
		});
		const updatedUser = { ...selectedUser };
		updatedUser.courses = userCourses;
		setSelectedUser(updatedUser);
	};

	//handle user product expiration date
	const _handleToggleUserProductExpirationDate = (date, id) => {
		const userCourses = selectedUser?.courses?.map(course => {
			const { expirationDate: _date, recordId } = course;
			let updatedExpirationDate = _date;
			if (id === recordId) updatedExpirationDate = date;
			return { ...course, expirationDate: updatedExpirationDate, id: recordId };
		});
		const updatedUser = { ...selectedUser };
		updatedUser.courses = userCourses;
		setSelectedUser(updatedUser);
	};

	//handle course transfer user selection.
	const _handleUserSelection = id => {
		setTransferCourseData({ ...transferCourseData, userId: null }); //reset data

		if (!id) {
			addNotification(getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_user_selection_error_message`), NOTIFICATION_TYPES.ERROR, 8000);

			return;
		}

		if (selectedUser?.userId === id) {
			addNotification(getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_courses_transfer_error_message`), NOTIFICATION_TYPES.ERROR, 8000);

			return;
		}

		setTransferCourseData({ ...transferCourseData, userId: id });
	};

	//handle transfer courses to specific user.
	const _handleTransferCourses = async () => {
		if (!transferCourseData?.userId) {
			addNotification(getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_user_selection_error_message`), NOTIFICATION_TYPES.ERROR, 8000);

			return;
		}
		try {
			const _recordIdsForTransfer = transferCourseData?.coursesList?.filter(c => c.isTransfer)?.map(c => c.courseRecordId);

			if (!_recordIdsForTransfer || !_recordIdsForTransfer.length) {
				addNotification(
					getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_course_selection_error_message`),
					NOTIFICATION_TYPES.ERROR,
					8000
				);

				return;
			}

			const result = await Api.TransferCoursesToUserId({
				userId: transferCourseData.userId,
				coursesRecordIds: _recordIdsForTransfer,
			});

			if (!result || !result.isUpdated) {
				addNotification(getTranslation(TRANSLATION_KEY, result.message), NOTIFICATION_TYPES.ERROR, 8000);

				return;
			}

			//update the selected user's courses.
			const _selectedUserCourses = selectedUser?.courses?.filter(c => !_recordIdsForTransfer.includes(c.recordId));
			setSelectedUser({ ...selectedUser, courses: _selectedUserCourses });

			addNotification(getTranslation(TRANSLATION_KEY, result.message), NOTIFICATION_TYPES.SUCCESS, 8000);
		} catch (err) {
			console.warn(err);
		}
	};

	//handle course selection for transfer.
	const _handleToggleCourseSelection = (e, courseRecordId) => {
		const _isTransfer = e?.target?.checked;
		const _coursesList = transferCourseData?.coursesList?.map(c =>
			courseRecordId === c.courseRecordId ? { ...c, isTransfer: _isTransfer } : c
		);
		const _transferCourseData = { ...transferCourseData, coursesList: _coursesList };
		setTransferCourseData(_transferCourseData);
	};

	//set the transfer courses array with all the user's courses.
	useEffect(() => {
		const _transferCoursesList =
			selectedUserCoursesList
				?.filter(c => c.isNewCourseStatus) //Only courses in new status.
				?.map(c => {
					return { courseRecordId: c.recordId, courseId: c.courseId, courseName: c.courseName, isTransfer: false };
				}) || [];
		setTransferCourseData({ userId: transferCourseData?.userId, coursesList: _transferCoursesList });
	}, [selectedUserCoursesList]);

	//fetch userList
	useEffect(() => {
		//fetch only when has exactly 3 charcters
		if (wordEntered.length > 3) return;
		if (wordEntered.length < 3) {
			setUsersList([]);
			return;
		}
		async function fetchData() {
			try {
				const _usersList = await Api.GetUsersList(wordEntered);
				if (!_usersList || !_usersList.length > 1) return;
				setUsersList(_usersList.map(({ userName, userId }) => ({ id: userId, description: userName })));
			} catch (exception) {}
		}
		fetchData();
	}, [wordEntered, Api]);

	//init get users for New Update Requests sections
	useEffect(() => {
		(async () => {
			const _infoChangeUsers = await Api.GetMyCourseEditFormDetailsApplicantList();
			if (_infoChangeUsers) {
				setRequestUpdateUsers(_infoChangeUsers);
			}
		})();
	}, []);

	//select user handler
	const selectUser = async id => {
		if (!id) return;
		setSelectedUser({});
		setTransferCourseData({ userId: null, coursesList: [] }); //reset data.
		try {
			const _selectedUser = await Api.GetUserDetailsByUserId(id);
			if (!_selectedUser || _selectedUser.length < 1) return;
			setSelectedUser({ ..._selectedUser, userId: id });
			setSelectedCourse([]);
		} catch (err) {
			console.warn(err);
		}
	};

	//search bar onChange value handler
	const _handleOnChange = value => {
		setWordEntered(value);
	};

	//update user details
	const _updateUserDetails = async e => {
		try {
			e?.preventDefault();
			if (!selectedUser) return;
			const form = new FormData(e?.target);
			const userDetails = Object.fromEntries(form.entries());

			setErrorMessageSide('first-loader');

			const ALPHABETIC_KEYS = ['firstName', 'middleName', 'lastName'];

			if (!VALIDATIONS.EMAIL.regex.test(userDetails['email'])) {
				addNotification(
					`${userDetails['email']} ${
						getTranslation(VALIDATIONS_TRANSLATION_KEY, VALIDATIONS.EMAIL.errorTranslationKey) || VALIDATIONS.EMAIL.error
					}`,
					NOTIFICATION_TYPES.ERROR,
					8000
				);

				return;
			}
			if (!VALIDATIONS.POSITIVE_NUMBER_WITH_ZERO.regex.test(userDetails['phoneNumber'])) {
				addNotification(
					`${userDetails['phoneNumber']} ${
						getTranslation(VALIDATIONS_TRANSLATION_KEY, VALIDATIONS.POSITIVE_NUMBER_WITH_ZERO.errorTranslationKey) ||
						VALIDATIONS.POSITIVE_NUMBER_WITH_ZERO.error
					}`,
					NOTIFICATION_TYPES.ERROR,
					8000
				);

				return;
			}
			if (!VALIDATIONS.MAX_LENGTH_20.regex.test(userDetails['phoneNumber'])) {
				addNotification(
					`${userDetails['phoneNumber']} ${
						getTranslation(VALIDATIONS_TRANSLATION_KEY, VALIDATIONS.MAX_LENGTH_20.errorTranslationKey) || VALIDATIONS.MAX_LENGTH_20.error
					}`,
					NOTIFICATION_TYPES.ERROR,
					8000
				);

				return;
			}
			for (const key in userDetails) {
				if (ALPHABETIC_KEYS.includes(key) && !VALIDATIONS.ALPHABETIC_WITH_SPACE.regex.test(userDetails[key])) {
					addNotification(
						`${userDetails[key]} ${
							getTranslation(VALIDATIONS_TRANSLATION_KEY, VALIDATIONS.ALPHABETIC_WITH_SPACE.errorTranslationKey) ||
							VALIDATIONS.ALPHABETIC_WITH_SPACE.error
						}`,
						NOTIFICATION_TYPES.ERROR,
						8000
					);

					return;
				}
			}

			userDetails.courses = selectedUser.courses;
			userDetails.userId = selectedUser.userId;

			const res = await Api.UpdateUserDetails(userDetails);

			if (res?.isUpdate) {
				setSelectedUser(userDetails);

				addNotification(getTranslation(TRANSLATION_KEY, res.message), NOTIFICATION_TYPES.SUCCESS, 8000);
			} else {
				addNotification(getTranslation(TRANSLATION_KEY, res.message), NOTIFICATION_TYPES.ERROR, 8000);
			}
		} catch (error) {
			console.warn(error);
		}
	};

	//handle onChange TriggerMail choose option
	const getEmailsByCourse = ({ target }) => {
		const _courseId = target.value;
		if (!_courseId) setSelectedCourse([]);
		const _selectedCourse = selectedUser?.courses?.find(({ courseId }) => courseId === parseInt(_courseId));
		setSelectedCourse(_selectedCourse);
	};

	//export user course list
	const courseListNames = useMemo(() => {
		if (!selectedUser) return [];
		const productNames = selectedUser?.courses?.map(({ courseId, courseName, recordId, isActive }) => ({
			value: courseId,
			description: courseName,
			recordId: recordId,
			isActive: isActive,
		}));

		return productNames || [];
	}, [selectedUser]);

	//filter triggerMail options by isActive true
	const courseListSelectOptions = useMemo(() => {
		const isActiveCourses = courseListNames.filter(course => course.isActive === true);
		return isActiveCourses;
	}, [courseListNames]);
	//Send emails by recordId and emailTypeId
	const _handleSendTriggerMails = async e => {
		setErrorMessageSide('second-loader');
		try {
			e?.preventDefault();

			if (!selectedUser) return;
			const form = new FormData(e.target);
			const triggerMails = Object.fromEntries(form.entries());
			const { recordId } = selectedCourse;
			const emailTypeId = triggerMails.course_radio;

			if (!emailTypeId || !recordId) {
				addNotification(getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_trigger_mail_error_message`), NOTIFICATION_TYPES.ERROR, 8000);

				return;
			}
			if (emailTypeId === 'reset_timer') {
				const resetResponse = await Api.ResetTestTimerIELTS(selectedUser.userId, recordId);
				if (resetResponse) {
					addNotification(
						getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_reset_timer_success_message`),
						NOTIFICATION_TYPES.SUCCESS,
						8000
					);
				} else {
					addNotification(getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_reset_timer_error_message`), NOTIFICATION_TYPES.ERROR, 8000);
				}
				return;
			}

			if (emailTypeId === 'reset_code') {
				const resetResponse = await Api.ResetActivationCodeESL(selectedUser.userId, recordId);
				if (resetResponse) {
					addNotification(
						getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_reset_code_success_message`),
						NOTIFICATION_TYPES.SUCCESS,
						8000
					);
				} else {
					addNotification(getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_reset_code_error_message`), NOTIFICATION_TYPES.ERROR, 8000);
				}
				return;
			}

			const res = await Api.ResendTriggerEmail(emailTypeId, recordId);

			if (res) {
				addNotification(getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_email_success_message`), NOTIFICATION_TYPES.SUCCESS, 8000);
			} else addNotification(getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_email_error_message`), NOTIFICATION_TYPES.ERROR, 8000);
		} catch (error) {
			console.warn(error);
		}
	};

	//renders active toggle component for emails
	const _renderCustomerProductDetails = ({ courseName, isActive, expirationDate, recordId }, idx) => {
		return (
			<CustomerProductDetails
				key={idx}
				id={recordId}
				name={courseName}
				status={isActive}
				date={expirationDate}
				data={selectedUserCoursesList}
				toggleActive={_handleToggleUserProductStatus}
				updateExpirationDate={_handleToggleUserProductExpirationDate}
			/>
		);
	};

	const _renderCourseTransfer = ({ courseName, courseRecordId, isTransfer }, idx) => {
		return (
			<div className="course_transfer" key={idx}>
				<div className="course_transfer__checkbox">
					<input type="checkbox" id="checkbox" checked={isTransfer} onChange={e => _handleToggleCourseSelection(e, courseRecordId)} />
				</div>
				<div className="course_transfer__name">{courseName}</div>
			</div>
		);
	};

	//handle copy paste event on search bar
	const _handleOnPaste = e => {
		async function fetchData() {
			try {
				const PastedValue = e.clipboardData.getData('Text').replace('[ \t]+$').trim();
				const _userEmail = await Api.GetUsersList(PastedValue);
				if (!_userEmail || !_userEmail.length > 1) return;
				const email = _userEmail.map(({ userName, userId }) => ({ id: userId, description: userName }));
				setUsersList(email);
			} catch (ex) {
				console.warn(ex);
			}
		}
		fetchData();
	};

	//handles user order enrollment section
	const _enrollUserByOrderId = async () => {
		setErrorMessageSide('third-loader');
		try {
			if (!enrollOrderValue) {
				addNotification(getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_enroll_user_error_message`), NOTIFICATION_TYPES.ERROR, 8000);

				return;
			}
			const res = await Api.InsertUpdateUsersList(enrollOrderValue);
			if (res.isUpdated) {
				addNotification(getTranslation('register', res.message), NOTIFICATION_TYPES.SUCCESS, 8000);
			} else addNotification(getTranslation('register', res.message), NOTIFICATION_TYPES.ERROR, 8000);
		} catch (ex) {
			addNotification(getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_enroll_user_failed_message`), NOTIFICATION_TYPES.ERROR, 8000);
			console.warn(ex);
		}
	};
	//generates silent login path for review page
	const _generateReviewLink = async e => {
		try {
			e.preventDefault();
			if (!selectedUser || selectedUser.length < 1) return;
			if (!selectedCourse || !selectedCourse.recordId) return;
			const _pathToReturn = `${process.env.REACT_APP_API_URL}/app${ROUTES.SILENT_LOGIN}?u=${selectedUser.email}&p=${selectedUser.password}&r=${selectedCourse?.recordId}&t=review`;
			if (navigator.clipboard) {
				navigator.clipboard.writeText(_pathToReturn);
				if (e) toolTipRef.current.Show({ x: e.pageX || -10, y: e.pageY || -10 });
			}
		} catch (err) {
			console.error('Clipboard write threw an error:', err);
		}
	};

	//extarct only the details that the user changed from is original details
	const requestUpdateExtractedDetails = useMemo(() => {
		if (
			(updateRequestOriginalData && !Object.keys(updateRequestOriginalData).length) ||
			(updateRequestSelectedStudent && !Object.keys(updateRequestSelectedStudent).length)
		)
			return [];
		let newDetails = {
			fname: updateRequestSelectedStudent.fname,
			lname: updateRequestSelectedStudent.lname,
			phone: updateRequestSelectedStudent.phoneNumber,
			email: updateRequestSelectedStudent.changeToEmail,
			userId: updateRequestSelectedStudent.userId,
		};

		const unmatchedValues = Object.keys(updateRequestOriginalData).reduce((differences, key) => {
			if (updateRequestOriginalData[key] !== newDetails[key]) {
				differences[key] = newDetails[key];
			}
			return differences;
		}, {});
		return unmatchedValues;
	}, [updateRequestOriginalData, updateRequestSelectedStudent]);

	//handles New Update Requests table user onClick, also fetches the original user data before sending the info change request
	const _handleNewUpdateRequestSelectStudent = async row => {
		setUpdateRequestSelectedStudent(row.original);
		if (row.original.userId) {
			const _userId = row.original.userId || 0;
			const originalUserData = await Api.GetUserDetailsByUserId(_userId);
			if (originalUserData) {
				const originalDetails = {
					fname: originalUserData.firstName,
					lname: originalUserData.lastName,
					email: originalUserData.email,
					phone: originalUserData.phoneNumber,
					userId: originalUserData.userId,
				};
				setUpdateRequestOriginalData(originalDetails);
				setUpdateRequestSendParameters(originalDetails); //prepare object from the original to handle unchanged user details
			}
		}
	};
	//handles rejection of update details
	const _handleRejectUpdateDetailsRequest = async () => {
		const _rejectRes = await Api.UpdateMyCourseFormStatus({
			userId: updateRequestSendParameters.userId,
			isRejected: true,
		});
		if (_rejectRes.isUpdated) {
			return true;
		}
		return false;
	};

	//handle New Update Requests form Apply Changes onClick
	const _handleUpdateDetailsRequest = async e => {
		try {
			e?.preventDefault();

			const hasParameters = (updateRequestSendParameters && Object.keys(updateRequestSendParameters).length) || 0;
			if (!hasParameters) return;

			const res = await Api.UpdateMyCourseFormStatus(updateRequestSendParameters);
			if (res.isUpdated) {
				const _updatedInfoChangeUsers = await Api.GetMyCourseEditFormDetailsApplicantList();
				if (_updatedInfoChangeUsers) {
					setRequestUpdateUsers(_updatedInfoChangeUsers);
					addNotification(
						getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_update_details_success_message`),
						NOTIFICATION_TYPES.SUCCESS,
						8000
					);
				}
				//resets all states for new selection
				_handleUpdateRequestCancelClick();
				return;
			}

			addNotification(getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_update_details_error_message`), NOTIFICATION_TYPES.ERROR, 8000);
		} catch (error) {
			console.warn(error);
		}
	};

	//handle Reject button
	const _handleRejectOnClick = async () => {
		const rejectRes = await _handleRejectUpdateDetailsRequest();
		if (!rejectRes) {
			addNotification(getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_update_details_error_message`), NOTIFICATION_TYPES.ERROR, 8000);
			return;
		}
		const _updatedInfoChangeUsers = await Api.GetMyCourseEditFormDetailsApplicantList();
		if (!_updatedInfoChangeUsers) return;
		setRequestUpdateUsers(_updatedInfoChangeUsers);
		addNotification(getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_reject_success_message`), NOTIFICATION_TYPES.SUCCESS, 8000);

		_handleUpdateRequestCancelClick();
	};

	//resets the selected student and related states
	const _handleUpdateRequestCancelClick = () => {
		setUpdateRequestSelectedStudent({});
		setUpdateRequestApprovedUserFields([]);
		setUpdateRequestSendParameters({});
	};

	//handle update requests V click
	const _handleApproveFieldOnClick = key => {
		setUpdateRequestSendParameters({
			...updateRequestSendParameters,
			[key]: requestUpdateExtractedDetails[key],
		});
		if (!updateRequestApprovedUserFields.includes(key)) {
			const newKeys = [...updateRequestApprovedUserFields, key];
			setUpdateRequestApprovedUserFields(newKeys);
		}
	};
	//handle update requests X click
	const _handleRemoveFieldOnClick = key => {
		setUpdateRequestSendParameters({
			...updateRequestSendParameters,
			[key]: updateRequestOriginalData[key],
		});
		setUpdateRequestApprovedUserFields(updateRequestApprovedUserFields.filter(_key => _key !== key));
	};
	const labelByKey = key => {
		return LABEL_TYPE[key];
	};

	return (
		<div className="AdvancedUserManagement">
			<Container>
				<div className="AdvancedUserManagement__enroll">
					<div className="AdvancedUserManagement__enroll__Input_wrapper">
						<Input
							label={getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_enroll_label`)}
							placeHolder={getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_enroll_placeholder`)}
							onChange={e => setEnrollOrderValue(e.target.value)}
						/>
					</div>
					<Button small title={getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_enroll_button`)} onClick={_enrollUserByOrderId} />
					{errorMessageSide === 'third-loader' && (
						<ChangesSpinner
							failed={responseMessage.failed}
							success={!responseMessage.failed}
							visible={responseMessage.visible}
							extraMessage={responseMessage.message}
						/>
					)}
				</div>

				<div className="AdvancedUserManagement__update_requests">
					<div className="AdvancedUserManagement__update_requests__title">
						{getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_update_details_title`)}
					</div>
					<div className="AdvancedUserManagement__update_requests__container">
						<div className="table_wrapper">
							{requestUpdateUsers.length > 0 ? (
								<Table columns={requestUpdateUsersColumns} data={requestUpdateUsers} pageSizeProp={10} />
							) : (
								getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_update_details_no_data`)
							)}
						</div>
						{updateRequestSelectedStudent &&
							updateRequestOriginalData &&
							Object.keys(updateRequestSelectedStudent).length > 0 &&
							Object.keys(updateRequestOriginalData).length > 0 && (
								<div className="user_details">
									<div className="user_details__original">
										<div className="user_details__original__title">
											{getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_user_details_original_title`)}
										</div>
										{updateRequestOriginalData && Object.keys(updateRequestOriginalData).length > 0 && (
											<div className="user_details__original__content">
												<div className="user_details__original__content__field">
													{getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_update_details_email_field`)}{' '}
													<b>{updateRequestOriginalData.email}</b>
												</div>
												<div className="user_details__original__content__field">
													{getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_update_details_first_name_field`)}{' '}
													<b>{updateRequestOriginalData.fname}</b>
												</div>
												<div className="user_details__original__content__field">
													{getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_update_details_last_name_field`)}{' '}
													<b>{updateRequestOriginalData.lname}</b>
												</div>
												<div className="user_details__original__content__field">
													{getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_update_details_phone_field`)}{' '}
													<b>{updateRequestOriginalData.phone}</b>
												</div>
											</div>
										)}
									</div>

									<div className="user_details__changed">
										<div className="user_details__changed__title">
											{getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_user_details_changed_title`)}
										</div>
										<div className="inputs_wrapper">
											{requestUpdateExtractedDetails &&
												Object.keys(requestUpdateExtractedDetails).length > 0 &&
												Object.keys(requestUpdateExtractedDetails).map((key, idx) => (
													<div className="inputs_wrapper__changed_inputs" key={idx}>
														<Input
															key={generateId(15)}
															name={key}
															label={labelByKey(key)}
															defaultValue={requestUpdateExtractedDetails[key]}
															disabled={updateRequestApprovedUserFields.includes(key)}
														/>
														<div className="btn_wrapper">
															{updateRequestApprovedUserFields.includes(key) ? (
																<Icon icon="doneIcon" size={25} color="#000" />
															) : (
																<Icon icon="checkmark" size={25} onClick={() => _handleApproveFieldOnClick(key)} />
															)}

															<Icon icon="closeIcon" size={23} onClick={() => _handleRemoveFieldOnClick(key)} />
														</div>
													</div>
												))}
											<div className="btn_container">
												<Button
													title={getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_user_details_apply_button`)}
													small
													onClick={_handleUpdateDetailsRequest}
												/>
												<Button
													title={getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_user_details_reject_button`)}
													small
													onClick={_handleRejectOnClick}
												/>
												<Button
													title={getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_user_details_cancel_button`)}
													small
													onClick={_handleUpdateRequestCancelClick}
												/>
											</div>
										</div>
									</div>
								</div>
							)}
					</div>
				</div>

				<div className="AdvancedUserManagement__search">
					<AutoCompleteSelect
						label={getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_search_label`)}
						data={usersList}
						onChange={_handleOnChange}
						onSelect={selectUser}
						onPaste={_handleOnPaste}
					/>
				</div>

				{selectedUser?.email && (
					<>
						<div className="AdvancedUserManagement__user_details">
							<form onSubmit={_updateUserDetails}>
								<Input
									label={getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_user_details_first_name_field`)}
									defaultValue={selectedUser.firstName || ''}
									name="firstName"
								/>
								<Input
									label={getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_user_details_middle_name_field`)}
									defaultValue={selectedUser.middleName || ''}
									name="middleName"
								/>
								<Input
									label={getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_user_details_last_name_field`)}
									defaultValue={selectedUser.lastName || ''}
									name="lastName"
								/>
								<Input
									label={getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_user_details_email_field`)}
									defaultValue={selectedUser.email || ''}
									name="email"
								/>
								<div className="phone_wrapper">
									<Input
										label={getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_user_details_phone_code_field`)}
										defaultValue={selectedUser.phoneCountryCode || ''}
										name="phoneCountryCode"
									/>
									<Input
										label={getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_user_details_phone_number_field`)}
										defaultValue={selectedUser.phoneNumber || ''}
										name="phoneNumber"
									/>
								</div>
								<Select
									label={getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_user_details_status_field`)}
									defaultValue={selectedUser.userApplicationStatusId || ''}
									name="userApplicationStatusId"
									options={ddls?.userApplicationStatus?.options || []}
								/>

								{(selectedUserCoursesList?.length && (
									<div className="AdvancedUserManagement__CustomerProductDetails_wrapper">
										{selectedUserCoursesList && (
											<div className="AdvancedUserManagement__CustomerProductDetails_wrapper__row">
												<div className="AdvancedUserManagement__CustomerProductDetails_wrapper__row__title">
													{getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_user_details_course_field_title`)}
												</div>
												<div className="AdvancedUserManagement__CustomerProductDetails_wrapper__row__title">
													{getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_user_details_course_field_status`)}
												</div>
												<div className="AdvancedUserManagement__CustomerProductDetails_wrapper__row__title">
													{getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_user_details_course_field_date`)}
												</div>
											</div>
										)}
										{selectedUserCoursesList?.map(_renderCustomerProductDetails)}
									</div>
								)) ||
									null}
								<div className="flex">
									<Button small title={getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_user_details_button`)} />

									{errorMessageSide === 'first-loader' && (
										<ChangesSpinner
											failed={responseMessage.failed}
											success={!responseMessage.failed}
											visible={responseMessage.visible}
											extraMessage={responseMessage.message}
										/>
									)}
								</div>
							</form>
						</div>
						<div className="AdvancedUserManagement__send_email">
							<form className="TriggerMail" onSubmit={_handleSendTriggerMails}>
								<div className="TriggerMail__title"></div>
								<div className="TriggerMail__choose_product">
									<Select
										label={getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_trigger_mail_search_label`)}
										options={courseListSelectOptions}
										onChange={getEmailsByCourse}
										placeHolder={getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_trigger_mail_search_placeholder`)}
									/>
								</div>
								<div className="TriggerMail__grid">
									{selectedCourseEmails?.map(({ emailName, emailTypeId }, key) => {
										const id = `${emailTypeId}-${emailName}`;
										return (
											<div className="radio" key={key}>
												<input type="radio" id={id} name="course_radio" value={emailTypeId} />
												<label htmlFor={id}>{emailName}</label>
											</div>
										);
									})}
								</div>
								<div className="flex">
									<Button small title={getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_trigger_mail_send_button`)} />
									{errorMessageSide === 'second-loader' && (
										<ChangesSpinner
											failed={responseMessage.failed}
											success={!responseMessage.failed}
											visible={responseMessage.visible}
											extraMessage={responseMessage.message}
										/>
									)}
									<Button
										small
										title={getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_trigger_mail_copy_button`)}
										onClick={_generateReviewLink}
									/>
								</div>
							</form>
						</div>{' '}
						{(transferCourseData?.coursesList?.length && (
							<div className="AdvancedUserManagement__course_transfer">
								<div className="AdvancedUserManagement__course_transfer__title">
									{getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_course_transfer_title`)}
								</div>
								<div className="AdvancedUserManagement__search">
									<AutoCompleteSelect
										label={getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_course_transfer_search_label`)}
										data={usersList}
										onChange={_handleOnChange}
										onSelect={_handleUserSelection}
										onPaste={_handleOnPaste}
										placeholder={getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_course_transfer_search_placeholder`)}
										withErrorMessage
									/>
								</div>
								{transferCourseData?.userId && (
									<>
										<div className="AdvancedUserManagement__course_transfer__table">
											{selectedUserCoursesList && (
												<div className="AdvancedUserManagement__course_transfer__row">
													<div className="AdvancedUserManagement__course_transfer__row__title"></div>
													<div className="AdvancedUserManagement__course_transfer__row__title">
														{getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_course_transfer_row_title`)}
													</div>
												</div>
											)}
											{transferCourseData?.coursesList?.map(_renderCourseTransfer)}
										</div>
										<Button
											small
											title={getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_course_transfer_button`)}
											onClick={_handleTransferCourses}
										></Button>
									</>
								)}
							</div>
						)) ||
							null}
						<Tooltip ref={toolTipRef}>{getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_tooltip`)}</Tooltip>
					</>
				)}
			</Container>
		</div>
	);
};

export default AdvancedUserManagement;
